import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  SurveyCreateDto,
  SurveyDto,
  SurveyListItemDto,
  SurveyParticipantDto,
  SurveyUpdateDto
} from '../models';
import {
  MOCK_SURVEYS,
  buildMockRespondentToken,
  getMockParticipants,
  getMockSurveys
} from '../../mock/work-environment-survey.mock-data';

/**
 * Surveys and their participants.
 *
 * NOT YET CONNECTED TO THE BACKEND (#6073). Every method returns mock data
 * from `mock/work-environment-survey.mock-data.ts` and documents the endpoint
 * that will replace it. Wiring this up means injecting `HttpClient`, swapping
 * each `of(...)` for the documented call, and deleting the mock file — the
 * signatures are already the ones the components will keep using.
 */
@Injectable()
export class WorkEnvironmentSurveyService {
  private readonly API_BASE = `${environment.BASE_URL_V2}/work-environment-survey`;

  /** Simulated round-trip, so loading states are actually exercised. */
  private readonly mockLatencyMs = 250;

  /**
   * All surveys for a business, newest first.
   *
   * GET {API_BASE}/business/{businessId}/surveys
   */
  getList(businessId: number): Observable<SurveyListItemDto[]> {
    return of(getMockSurveys(businessId)).pipe(
      map(surveys =>
        [...surveys]
          .sort((a, b) => b.StartDate.localeCompare(a.StartDate))
          .map(survey => ({
            Id: survey.Id,
            Title: survey.Title,
            StartDate: survey.StartDate,
            EndDate: survey.EndDate,
            ReminderDate: survey.ReminderDate,
            ParticipantCount: survey.ParticipantIds.length
          }))
      ),
      delay(this.mockLatencyMs)
    );
  }

  /**
   * One survey with its participant assignment.
   *
   * GET {API_BASE}/surveys/{id}
   */
  getById(id: number): Observable<SurveyDto | null> {
    const matches = MOCK_SURVEYS.filter(survey => survey.Id === id);

    return of(matches.length > 0 ? matches[0] : null).pipe(
      delay(this.mockLatencyMs)
    );
  }

  /**
   * Active employees of the business, as candidate participants.
   *
   * GET {API_BASE}/business/{businessId}/participants
   */
  getParticipants(businessId: number): Observable<SurveyParticipantDto[]> {
    return of(getMockParticipants(businessId)).pipe(delay(this.mockLatencyMs));
  }

  /**
   * Create a survey and assign its participants.
   *
   * POST {API_BASE}/business/{businessId}/surveys
   */
  create(businessId: number, dto: SurveyCreateDto): Observable<{ Id: number }> {
    const id = Math.max(0, ...MOCK_SURVEYS.map(survey => survey.Id)) + 1;

    MOCK_SURVEYS.push({
      Id: id,
      BusinessId: businessId,
      Title: dto.Title,
      StartDate: dto.StartDate,
      EndDate: dto.EndDate,
      ReminderDate: dto.ReminderDate,
      ParticipantIds: [...dto.ParticipantIds]
    });

    return of({ Id: id }).pipe(delay(this.mockLatencyMs));
  }

  /**
   * Update a survey. Replaces the participant assignment wholesale.
   *
   * PUT {API_BASE}/surveys/{id}
   */
  update(id: number, dto: SurveyUpdateDto): Observable<void> {
    const matches = MOCK_SURVEYS.filter(item => item.Id === id);

    if (matches.length > 0) {
      const survey = matches[0];
      survey.Title = dto.Title;
      survey.StartDate = dto.StartDate;
      survey.EndDate = dto.EndDate;
      survey.ReminderDate = dto.ReminderDate;
      survey.ParticipantIds = [...dto.ParticipantIds];
    }

    return of(undefined).pipe(delay(this.mockLatencyMs));
  }

  /**
   * In-app path a respondent follows to answer this survey.
   *
   * TEMPORARY: the token is built client-side from the survey id, so the whole
   * survey shares one link. A real token is an opaque per-respondent GUID that
   * the backend creates when a survey is distributed. AMIL generated those
   * GUIDs but never linked them to a participant and never handed them out —
   * no e-mail was sent and no link was shown anywhere — so nothing here can
   * produce a per-person link yet. See #6073.
   */
  getRespondentPath(surveyId: number): string {
    return `/work-environment-survey/respondent/${buildMockRespondentToken(
      surveyId
    )}`;
  }

  /**
   * Delete a survey and the responses collected for it.
   *
   * DELETE {API_BASE}/surveys/{id}
   */
  delete(id: number): Observable<void> {
    const index = MOCK_SURVEYS.findIndex(survey => survey.Id === id);

    if (index > -1) {
      MOCK_SURVEYS.splice(index, 1);
    }

    return of(undefined).pipe(delay(this.mockLatencyMs));
  }
}
